import { createContext, useContext } from 'react';

/**
 * The identity + session action the app shell's sidebar footer renders.
 *
 * The access-control gates (SsoGate / TokenGate) own the sign-out / connection
 * logic and the user identity; they publish it through this context so the
 * shared `Layout` sidebar can host the user row + sign-out button. This keeps
 * the auth/data flow exactly where it was — the footer button just relocates
 * the previously fixed bottom-right control into the sidebar.
 */
export interface SessionActionValue {
  /** Fired by the sidebar footer button (sign out, or edit the connection). */
  onAction: () => void;
  /** aria-label for the footer button. */
  actionLabel: string;
  /** Tooltip for the footer button (e.g. the billable-lock explanation). */
  actionTitle?: string;
  /** Disables the footer button while a billable request is in flight. */
  disabled?: boolean;
  /** Primary display name for the user row. */
  userName?: string;
  /** Secondary identity line (email, or the connected Worker URL). */
  userEmail?: string;
}

const SessionActionContext = createContext<SessionActionValue | null>(null);

export const SessionActionProvider = SessionActionContext.Provider;

/** Read the current session action, or `null` outside a gate provider. */
export function useSessionAction(): SessionActionValue | null {
  return useContext(SessionActionContext);
}

export default SessionActionContext;
